import axios, { HttpStatusCode } from "axios";
import { customFetch } from "../lib/network/Axios";
import { handleError } from "../lib/ErrorHandler";
import {
  CreatePromotionPayload,
  Promotion,
  UpdatePromotionPayload,
} from "../types/promotion.type";

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data?.message === "string" && data.message.trim()) return data.message;
    if (Array.isArray(data?.message) && data.message.length > 0) {
      const first = data.message[0];
      if (typeof first === "string" && first.trim()) return first;
    }
    if (typeof data === "string" && data.trim()) return data;
    if (typeof error.message === "string" && error.message.trim()) return error.message;
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
};

const unwrapCampaignList = (data: any): Promotion[] => {
  if (Array.isArray(data?.campaigns)) return data.campaigns as Promotion[];
  if (Array.isArray(data)) return data as Promotion[];
  return [];
};

const unwrapCampaign = (data: any): Promotion | null => {
  if (data?.campaign) return data.campaign as Promotion;
  if (data?.savedRecord) return data.savedRecord as Promotion;
  if (data?.updatedCampaign) return data.updatedCampaign as Promotion;
  if (data?.deletedCampaign) return data.deletedCampaign as Promotion;
  if (data?.deactivatedCampaign) return data.deactivatedCampaign as Promotion;
  if (data?.activatedCampaign) return data.activatedCampaign as Promotion;
  if (data && typeof data === "object" && "id" in data) return data as Promotion;
  return null;
};

export const getPromotions = async (): Promise<Promotion[]> => {
  try {
    const response = await customFetch.get("/qr/campaigns/all");
    if (response?.status === HttpStatusCode.Ok) {
      return unwrapCampaignList(response.data);
    }
    return [];
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to load promotions"));
  }
};

export const createPromotion = async (
  payload: CreatePromotionPayload,
): Promise<Promotion> => {
  try {
    const response = await customFetch.post("/qr/campaign/save", {
      ...payload,
      donationCount: payload.donationCount ?? 0,
    });
    if (
      response?.status === HttpStatusCode.Created ||
      response?.status === HttpStatusCode.Ok
    ) {
      const record = unwrapCampaign(response.data);
      if (record) return record;
    }
    throw new Error("Failed to create promotion");
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to create promotion"));
  }
};

export const updatePromotion = async (
  payload: UpdatePromotionPayload,
): Promise<Promotion> => {
  try {
    const response = await customFetch.put("/qr/campaign/update", payload);
    if (response?.status === HttpStatusCode.Ok) {
      const record = unwrapCampaign(response.data);
      if (record) return record;
    }
    throw new Error("Failed to update promotion");
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to update promotion"));
  }
};

export const deletePromotion = async (id: number): Promise<Promotion> => {
  try {
    const response = await customFetch.put(`/qr/campaign/delete?id=${id}`);
    if (response?.status === HttpStatusCode.Ok) {
      const record = unwrapCampaign(response.data);
      if (record) return record;
    }
    throw new Error("Failed to delete promotion");
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to delete promotion"));
  }
};

export const deactivatePromotion = async (id: number): Promise<Promotion> => {
  try {
    const response = await customFetch.put(`/qr/campaign/deactivate?id=${id}`);
    if (response?.status === HttpStatusCode.Ok) {
      const record = unwrapCampaign(response.data);
      if (record) return record;
    }
    throw new Error("Failed to deactivate promotion");
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to deactivate promotion"));
  }
};

export const activatePromotion = async (id: number): Promise<Promotion> => {
  try {
    const response = await customFetch.put(`/qr/campaign/activate?id=${id}`);
    if (response?.status === HttpStatusCode.Ok) {
      const record = unwrapCampaign(response.data);
      if (record) return record;
    }
    throw new Error("Failed to activate promotion");
  } catch (error) {
    handleError(error);
    throw new Error(extractApiErrorMessage(error, "Failed to activate promotion"));
  }
};
